import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import customInactive from '../../../assets/plans/custom-inactive.svg';
import customActive from '../../../assets/plans/custom-active.svg';

import {
  isPaycheck,
} from '../../../util';
import { MODAL_RELATION_LIST } from '../../../constants';
import { useCalculatePlanPrice, useProp103Features } from '../../../hooks';
import {
  CUSTOM_PLAN_ID,
  WHATS_COVERED_LINK_ID,
  prepackagedPlansIcons,
} from './constants';
import {
  needToKnowModalVisibleChanged,
  saveDeductibleSelected,
  saveSelectedReimbursement,
  selectedPlan,
} from '../../../actions';

import { PrepackagedPlanCard } from './PrepackagedPlanCard';
import { WhatsCoveredLink } from '../WhatsCoveredLink';
import './PlanSection.css';
import { PlansSectionProp103 } from './PlansSectionProp103';
import {
  usePrepackageConsistency,
} from '../../../hooks/usePrepackageConsistency';

// TODO: paycheck only
const billingCycleLabel = {
  desktop: isPaycheck ? 'Per Paycheck' : 'Monthly Premium',
  mobile: isPaycheck ? '/paycheck' : '/month',
};

const { NEED_TO_KNOW_MODAL_ID } = MODAL_RELATION_LIST;

const PlansSectionRegular = () => {
  const { t } = useTranslation('quoting');
  const dispatch = useDispatch();
  const store = useSelector(({ quoting }) => quoting);

  const { multiModalData } = store;

  const { quoteSelected, plansPackaged, plans } = useCalculatePlanPrice();

  const isCustomPlanSelected = quoteSelected.selectedPlanPackaged
    === CUSTOM_PLAN_ID;

  function getFirstCategoryId(menuId, subnMenuId = null) {
    let categoryId = 1;

    if (multiModalData.InsuranceMultiModalSectionDtoList) {
      const categoryList = multiModalData
        .InsuranceMultiModalSectionDtoList
        .find((element) => element.Id === menuId);

      if (categoryList) {
        categoryId = categoryList
          .InsuranceMultiModalCategoryDtoList
          .find((sub) => sub.Id === subnMenuId || !subnMenuId).Id;
      }
    }

    return categoryId;
  }

  const showWhatCoverageModal = () => {
    const categoryId = getFirstCategoryId(NEED_TO_KNOW_MODAL_ID);
    dispatch(needToKnowModalVisibleChanged({ categoryId, visible: true }));
  };

  const onChangePrepackagedPlan = useCallback((plan) => ({
    planId,
    planAmount,
    planPackagedId,
  }) => {
    dispatch(selectedPlan({ planAmount, planId, planPackagedId }));
    dispatch(saveSelectedReimbursement({
      description: plan.reimbursementName,
      id: plan.ReimbursementId,
    }));
    dispatch(saveDeductibleSelected({
      description: plan.deductibleName,
      id: plan.DeductibleId,
    }));
  }, [dispatch]);

  const onChangeCustomPlan = useCallback(({
    planAmount,
    planId,
    planPackagedId,
  }) => {
    dispatch(selectedPlan({ planAmount, planId, planPackagedId }));
  }, [dispatch]);

  const customPlanDisclaimer = useMemo(() => {
    if (!store.petQuoteSelected || !store.data
      || !store.data.ebPetQuoteResponseList) {
      return '';
    }

    const byPet = store.data.ebPetQuoteResponseList
      .find((pet) => pet.petQuoteId === store.petQuoteSelected);

    return byPet?.PrepackagedPlanDisclaimer;
  }, [store.petQuoteSelected, store.data]);

  return (
    <>
      <p className="Plan-section-text">{t('prepackagedPlans.text')}</p>

      <WhatsCoveredLink
        ariaControls="seeWhatWeCovermodalContainer"
        className="Powerup-see-whats-covered-link-main-container-plans"
        id={WHATS_COVERED_LINK_ID}
        onClick={showWhatCoverageModal}
        text={t('prepackagedPlans.coverage')}
      />

      <div className="Plan-section-prepackaged-plans">
        {plansPackaged.map((plan, index) => (
          <PrepackagedPlanCard
            key={String(plan.Id)}
            activeIcon={prepackagedPlansIcons[index].active}
            billingCycleLabel={billingCycleLabel.mobile}
            description={plan.Description}
            inactiveIcon={prepackagedPlansIcons[index].inactive}
            maxAnnual={plan.maxAnnual}
            name={plan.Name}
            onPlanChange={onChangePrepackagedPlan(plan)}
            planId={plan.PlanId}
            planPackagedId={plan.Id}
            price={plan.monthlyPrice}
            selected={quoteSelected.selectedPlanPackaged === plan.Id}
          />
        ))}

        <PrepackagedPlanCard
          activeIcon={customActive}
          billingCycleLabel={billingCycleLabel.mobile}
          bottomDisclaimer={customPlanDisclaimer}
          inactiveIcon={customInactive}
          name={t('prepackagedPlans.customPlan')}
          onPlanChange={onChangeCustomPlan}
          planId={quoteSelected.selectedPlan}
          planPackagedId={CUSTOM_PLAN_ID}
          plans={plans}
          price={quoteSelected.totalMontly}
          quoteSelected={quoteSelected}
          selected={isCustomPlanSelected}
        />
      </div>
    </>
  );
};

export const PlansSection = () => {
  const shallUseProp103 = useProp103Features();
  usePrepackageConsistency();

  return (shallUseProp103
    ? <PlansSectionProp103 />
    : <PlansSectionRegular />
  );
};
